import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Users, Trophy, Building, DollarSign, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

const pillarImages = [
  "https://images.unsplash.com/photo-1665498847016-ac58ce9793ef?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1762053275412-03726506562a?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1770325266728-08535d630a11?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1720284272064-fbad7033bf8f?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1748003220533-f588a92b2a62?crop=entropy&cs=srgb&fm=jpg&q=85"
];

const Pillars = () => {
  const { t } = useLanguage();
  const [expandedPillar, setExpandedPillar] = useState(null);

  const iconMap = {
    Shield: Shield,
    Users: Users,
    Trophy: Trophy,
    Building: Building,
    DollarSign: DollarSign
  };

  const togglePillar = (id) => {
    setExpandedPillar(expandedPillar === id ? null : id);
  };

  const colors = [
    { from: 'from-blue-500', to: 'to-blue-600', shadow: 'shadow-blue-500/30', border: 'border-blue-500/50' },
    { from: 'from-cyan-500', to: 'to-cyan-600', shadow: 'shadow-cyan-500/30', border: 'border-cyan-500/50' },
    { from: 'from-red-500', to: 'to-red-600', shadow: 'shadow-red-500/30', border: 'border-red-500/50' },
    { from: 'from-orange-500', to: 'to-orange-600', shadow: 'shadow-orange-500/30', border: 'border-orange-500/50' },
    { from: 'from-purple-500', to: 'to-purple-600', shadow: 'shadow-purple-500/30', border: 'border-purple-500/50' }
  ];

  return (
    <section id="pillars" className="relative py-24 bg-gray-900">
      {/* Background Effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t.pillars.title}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t.pillars.subtitle}
          </p>
        </div>

        {/* Pillars Accordion */}
        <div className="space-y-6">
          {t.pillars.list.map((pillar, index) => {
            const Icon = iconMap[pillar.icon] || Shield;
            const colorScheme = colors[index % colors.length];
            const isExpanded = expandedPillar === pillar.id;

            return (
              <div
                key={pillar.id}
                className={`group relative bg-gray-950/50 backdrop-blur-sm border-2 rounded-2xl overflow-hidden transition-all duration-500 ${
                  isExpanded ? colorScheme.border : 'border-gray-800'
                } hover:shadow-xl ${isExpanded ? colorScheme.shadow : ''}`}
              >
                {/* Pillar Header */}
                <button
                  onClick={() => togglePillar(pillar.id)}
                  className="w-full p-6 sm:p-8 flex items-center justify-between text-left"
                >
                  <div className="flex items-center space-x-4 sm:space-x-6 flex-1">
                    <div className={`w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br ${colorScheme.from} ${colorScheme.to} rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${
                      isExpanded ? 'scale-110' : ''
                    }`}>
                      <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`text-2xl sm:text-3xl font-bold bg-gradient-to-r ${colorScheme.from} ${colorScheme.to} bg-clip-text text-transparent`}>
                          {String(pillar.id).padStart(2, '0')}
                        </span>
                        <h3 className="text-xl sm:text-2xl font-bold text-white">
                          {pillar.name}
                        </h3>
                      </div>
                      <p className="text-gray-400 leading-relaxed">
                        {pillar.description}
                      </p>
                    </div>
                  </div>
                  <div className={`ml-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                    {isExpanded ? (
                      <ChevronUp className="w-6 h-6 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Pillar Content */}
                <div
                  className={`transition-all duration-500 overflow-hidden ${
                    isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 sm:px-8 pb-8">
                    {/* Pillar Image */}
                    <div className="w-full h-48 rounded-xl overflow-hidden mb-6">
                      <img
                        src={pillarImages[index]}
                        alt={pillar.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="pl-0 sm:pl-4">
                      <h4 className="text-lg font-semibold text-white mb-4">Actions clés :</h4>
                      <ul className="space-y-3">
                        {pillar.actions.map((action, actionIndex) => (
                          <li key={actionIndex} className="flex items-start space-x-3 group/item">
                            <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 text-${colorScheme.from.split('-')[1]}-500`} />
                            <span className="text-gray-300 group-hover/item:text-white transition-colors duration-200">
                              {action}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pillars;
